
import { useState } from "react"
import { Search, MapPin, Users, Wifi, Shield, Car, Home, DollarSign, Star } from "lucide-react"


// Données des logements étudiants à Nairobi
const studentHousing = [
  // {
  //   id: 1,
  //   name: "University of Nairobi Halls",
  //   location: "University Way, Nairobi",
  //   type: "Résidence Universitaire",
  //   price: 15000,
  //   deposit: 30000,
  //   capacity: 4,
  //   image: "https://images.unsplash.com/photo-1555854877-bab0e564b8d5?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80",
  //   amenities: ["WiFi", "Sécurité 24h", "Cafétéria", "Bibliothèque", "Buanderie"],
  //   description: "Résidence officielle de l'Université de Nairobi, proche des salles de cours.",
  //   rating: 4.2,
  //   distance: "0.5 km de UoN",
  // }
]

// Composant Badge
const Badge = ({ children, variant = "default", className = "" }) => {
  const baseClasses = "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
  const variants = {
    default: "bg-blue-100 text-blue-800",
    secondary: "bg-gray-100 text-gray-800",
    outline: "border border-gray-300 text-gray-700 bg-white",
    success: "bg-green-100 text-green-800",
    warning: "bg-yellow-100 text-yellow-800",
  }

  return <span className={`${baseClasses} ${variants[variant]} ${className}`}>{children}</span>
}

// Composant Card
const Card = ({ children, className = "" }) => {
  return <div className={`bg-white rounded-lg border border-gray-200 shadow-sm ${className}`}>{children}</div>
}

const CardHeader = ({ children }) => {
  return <div className="p-6 pb-4">{children}</div>
}

const CardContent = ({ children }) => {
  return <div className="px-6 pb-6">{children}</div>
}

const CardTitle = ({ children, className = "" }) => {
  return <h3 className={`text-lg font-semibold leading-none tracking-tight ${className}`}>{children}</h3>
}

const CardDescription = ({ children, className = "" }) => {
  return <p className={`text-sm text-gray-600 ${className}`}>{children}</p>
}

// Composant Input
const Input = ({ className = "", ...props }) => {
  return (
    <input
      className={`flex h-10 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${className}`}
      {...props}
    />
  )
}

// Composant Button
const Button = ({ children, variant = "default", className = "", onClick, ...props }) => {
  const baseClasses =
    "inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none"
  const variants = {
    default: "bg-blue-600 text-white hover:bg-blue-700 h-10 px-4 py-2",
    outline: "border border-gray-300 bg-white hover:bg-gray-50 h-10 px-4 py-2",
  }

  return (
    <button className={`${baseClasses} ${variants[variant]} ${className}`} onClick={onClick} {...props}>
      {children}
    </button>
  )
}

function StudentHousing() {
  const [searchTerm, setSearchTerm] = useState("")
  const [selectedType, setSelectedType] = useState("All")
  const [priceRange, setPriceRange] = useState("All")

  const filteredHousing = studentHousing.filter((housing) => {
    const matchesSearch =
      housing.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      housing.location.toLowerCase().includes(searchTerm.toLowerCase()) ||
      housing.type.toLowerCase().includes(searchTerm.toLowerCase())

    const matchesType = selectedType === "All" || housing.type === selectedType

    const matchesPrice =
      priceRange === "All" ||
      (priceRange === "Budget" && housing.price <= 15000) ||
      (priceRange === "Moyen" && housing.price > 15000 && housing.price <= 25000) ||
      (priceRange === "Premium" && housing.price > 25000)

    return matchesSearch && matchesType && matchesPrice
  })

  const getTypeColor = (type) => {
    switch (type) {
      case "Résidence Universitaire":
        return "success"
      case "Studio":
        return "default"
      case "Appartement":
      case "Appartement Partagé":
        return "secondary"
      default:
        return "outline"
    }
  }

  return (
    <div className="min-h-screen mtCGfreeUniv dark:bg-gray-900 dark:text-gray-200 from-blue-50 to-purple-50">

        {/* Section Conseils */}
      <section className="bg-white mb-20 dark:bg-gray-800  mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-300 mb-8 text-center">Conseils pour Choisir votre Logement</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="bg-blue-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <MapPin className="h-8 w-8 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold dark:text-green-500 mb-2">Proximité Université</h3>
              <p className="text-gray-600 dark:text-gray-500 ">
                Choisissez un logement proche de votre université pour économiser sur les transports.
              </p>
            </div>
            <div className="text-center">
              <div className="bg-green-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <Shield className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold dark:text-green-500 mb-2">Sécurité</h3>
              <p className="text-gray-600 dark:text-gray-500 ">Privilégiez les logements avec sécurité 24h/24 dans des quartiers sûrs.</p>
            </div>
            <div className="text-center">
              <div className="bg-purple-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <DollarSign className="h-8 w-8 text-purple-600" />
              </div>
              <h3 className="text-lg font-semibold dark:text-green-500 mb-2">Budget</h3>
              <p className="text-gray-600 dark:text-gray-500 ">Calculez le coût total incluant loyer, caution, et frais annexes.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Header */}
      <header className="bg-white dark:bg-gray-900 dark:text-gray-200 shadow-sm ">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
              <Home className="h-8 w-8 text-blue-600" />
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div>
                <h1 className="text-2xl font-bold dark:text-blue-500">Logements  supplémentaires</h1>
                {/* <p className="text-gray-600 dark:text-gray-400 mt-1">Trouvez le logement parfait pour vos études</p> */}
              </div>
            </div>
            {/* <svg xmlns="http://www.w3.org/2000/svg" className="w-8 h-8 dark:text-green-500 text-green-500 stroke-current" fill="none" viewBox="0 0 24 24" strokeWidth="1.5"><path strokeLinecap="round" strokeLinejoin="round" d="M12 6L20 9l-8 3-8-3 8-3z" /><path strokeLinecap="round" strokeLinejoin="round" d="M12 6v12" /><path strokeLinecap="round" strokeLinejoin="round" d="M20 12a8.1 8.1 0 01-8 8 8.1 8.1 0 01-8-8" /></svg> */}
            {/* <div className="hidden md:flex items-center space-x-2">
              <div className="w-8 h-5 bg-black"></div>
              <div className="w-8 h-5 bg-red-600"></div>
              <div className="w-8 h-5 bg-green-600"></div>
            </div> */}
          </div>
        </div>
      </header>

      {/* Statistiques */}
      <section className="bg-white dark:bg-gray-900 ">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="text-3xl font-bold text-blue-600">{studentHousing.length}</div>
              <div className="text-gray-600">Logements</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-door dark:text-door-dark">
                KSh{" "}
                {Math.round(
                  studentHousing.reduce((acc, house) => acc + house.price, 0) / studentHousing.length,
                ).toLocaleString()}
              </div>
              <div className="text-gray-600">Prix Moyen</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-purple-600">
                {studentHousing.filter((house) => house.price <= 20000).length}
              </div>
              <div className="text-gray-600">Options Budget</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-orange-600">
                {studentHousing.reduce((acc, house) => acc + house.capacity, 0)}
              </div>
              <div className="text-gray-600">Places Totales</div>
            </div>
          </div>
        </div>
      </section>

      {/* Filtres et Recherche */}
      <section className="max-w-7xl mx-auto dark:bg-gray-900 px-2 sm:px-6 lg:px-8 py-8">
        <div className="bg-white dark:bg-gray-900 rounded-lg shadow-sm p-6 mb-8">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
              <Input
                type="text"
                placeholder="Rechercher par nom, localisation ou type..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 border-gray-300 dark:bg-gray-800"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              <Button className=" dark:text-gray-900" variant={selectedType === "All" ? "default" : "outline"} onClick={() => setSelectedType("All")}>
                Tous Types
              </Button>
              <Button
                className=" dark:text-gray-900"
                variant={selectedType === "Résidence Universitaire" ? "default" : "outline"}
                onClick={() => setSelectedType("Résidence Universitaire")}
              >
                Résidences
              </Button>
              <Button
                className=" dark:text-gray-900"
                variant={selectedType === "Studio" ? "default" : "outline"}
                onClick={() => setSelectedType("Studio")}
              >
                Studios
              </Button>
              <Button
                className=" dark:text-gray-900"
                variant={selectedType === "Appartement" ? "default" : "outline"}
                onClick={() => setSelectedType("Appartement")}
              >
                Appartements
              </Button>
            </div>
            <div className="flex gap-2 flex-wrap ">
              <Button className=" dark:text-gray-900" variant={priceRange === "All" ? "default" : "outline"} onClick={() => setPriceRange("All")}>
                Tous Prix
              </Button>
              <Button className=" dark:text-gray-900" variant={priceRange === "Budget" ? "default" : "outline"} onClick={() => setPriceRange("Budget")}>
                Budget
              </Button>
              <Button className=" dark:text-gray-900" variant={priceRange === "Moyen" ? "default" : "outline"} onClick={() => setPriceRange("Moyen")}>
                Moyen
              </Button>
              
              <Button className=" dark:text-gray-900"
                variant={priceRange === "Premium" ? "default" : "outline"}
                onClick={() => setPriceRange("Premium")}
              >
                Premium
              </Button>
            </div>
          </div>
        </div>

        {/* Liste des Logements */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {filteredHousing.map((housing) => (
            <Card key={housing.id} className="hover:shadow-lg rounded-lg border border-gray-100 shadow dark:border-gray-600 xl:p-1  dark:bg-gray-800 transition-shadow duration-300 overflow-hidden">
              {/* Image du logement */}
              <div className="relative rounded-t h-48 w-full overflow-hidden bg-gray-200">
                <img
                  src={housing.image || "/placeholder.svg"}
                  alt={`${housing.name}`}
                  className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                  onError={(e) => {
                    e.target.src =
                      "https://images.unsplash.com/photo-1555854877-bab0e564b8d5?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80"
                  }}
                />
                <div className="absolute top-4 right-4">
                  <Badge variant={getTypeColor(housing.type)}>{housing.type}</Badge>
                </div>
                <div className="absolute top-4 left-4">
                  <div className="bg-white/90 backdrop-blur-sm rounded-lg px-3 py-1">
                    <div className="flex items-center space-x-1">
                      <Star className="h-4 w-4 text-yellow-500 fill-current" />
                      <span className="text-sm dark:text-gray-900 font-medium">{housing.rating}</span>
                    </div>
                  </div>
                </div>
              </div>

              <CardHeader>
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <CardTitle className="text-xl dark:text-green-400 text-gray-900">{housing.name}</CardTitle>
                    <CardDescription className="flex items-center mt-2">
                      <MapPin className="h-4 dark:text-gray-200 w-4 mr-1" />
                      <span className="dark:text-gray-200" >

                      {housing.location}
                      </span>
                    </CardDescription>
                  </div>
                  <div className="text-right dark:text-gray-200">
                    <div className="text-2xl font-bold dark:text-gray-200 text-green-600">KSh {housing.price.toLocaleString()}</div>
                    <div className="text-sm dark:text-gray-200 text-gray-500">par mois</div>
                  </div>
                </div>
              </CardHeader>

              <CardContent>
                <p className="text-gray-600 dark:text-gray-200 mb-4">{housing.description}</p>

                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="flex items-center dark:text-gray-200 text-sm text-gray-600">
                    <Users className="h-4 dark:text-green-300 w-4 mr-2" />
                    {housing.capacity} {housing.capacity === 1 ? "personne" : "personnes"}
                  </div>
                  <div className="flex items-center text-sm dark:text-gray-200 text-gray-600">
                    <DollarSign className="h-4 dark:text-green-300 w-4 mr-2" />
                    Caution: KSh {housing.deposit.toLocaleString()}
                  </div>
                </div>

                <div className="mb-4">
                  <div className="text-sm dark:text-gray-200 text-gray-600 mb-2">{housing.distance}</div>
                </div>

                <div>
                  <h4 className="font-semibold dark:text-gray-200 text-xl text-gray-900 mb-2">Équipements :</h4>
                  <div className="flex flex-wrap gap-2">
                    {housing.amenities.map((amenity, index) => (
                      <Badge key={index} variant="outline" className="text-xs dark:bg-gray-700 dark:text-gray-100 dark:border-gray-700">
                        {amenity === "WiFi" && <Wifi className="h-3 w-3 mr-1" />}
                        {amenity === "Sécurité" && <Shield className="h-3 w-3 mr-1" />}
                        {amenity === "Parking" && <Car className="h-3 w-3 mr-1" />}
                        {amenity}
                      </Badge>
                    ))}
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t border-gray-200">
                  <div className="bg-blue-50 dark:bg-gray-700 rounded-lg p-3">
                    <h5 className="font-medium text-blue-900 dark:text-green-400 mb-1">Informations Indemnité</h5>
                    <div className="text-sm dark:text-gray-100 text-blue-700">
                      <div>• Caution remboursable: KSh {housing.deposit.toLocaleString()}</div>
                      <div>• Préavis: 30 jours</div>
                      <div>• Frais d'agence: 10% du loyer annuel</div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {filteredHousing.length === 0 && (
          <div className="text-center py-12">
            <Home className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun logement trouvé</h3>
            <p className="text-gray-600">Aucun logement supplémentaires trouvé</p>
          </div>
        )}
      </section>

    

      {/* Footer */}
     
    </div>
  )
}

export default StudentHousing
